import React from "react";
import format from "date-fns/format";
import isWithinInterval from "date-fns/isWithinInterval";
import getDate from "date-fns/getDate";
import getYear from "date-fns/getYear";
import lastDayOfMonth from "date-fns/lastDayOfMonth";
import uniqBy from "lodash/uniqBy";
import fromUnix from "date-fns/fromUnixTime";

import { isObject } from "../../lib/utils";

export const __EVENT_HIGHLIGHT_COLOR__ = "#28a745";
export const __DEFAULT_DATE_FORMAT__ = "yyyy/MM/dd";
export const __DEFAULT_TIME_FORMAT__ = "hh:mm a";

/**
 * The reason for wrapping a user provided custom input
 * into higher order component is to prevent react-datepicker's
 * error for React.forwardRef as they don't support functional
 * components as custom input.
 *
 * @param  {function} BaseComponent
 * @returns {class}
 */
export function createCustomInput(BaseComponent) {
  // eslint-disable-next-line react/prefer-stateless-function
  return class Default extends React.Component {
    render() {
      return <BaseComponent {...this.props} />;
    }
  };
}
/**
 * Sanitizes date inputs
 *
 * @param {any} date
 * @returns {Date|Error}
 */
export function getSanitizedDate(date) {
  if (!date) {
    return undefined;
  }
  if (typeof date === "number") {
    return fromUnix(date);
  }
  return new Date(date);
}

/**
 * Returns tooltip associated to a highlighted date
 *
 * @param  {object} outerNode
 * @param  {object|Date} innerNode
 */
export function getHighlightTooltip(outerNode, innerNode) {
  if (isObject(innerNode) && innerNode.tooltip) {
    return innerNode.tooltip;
  }

  if (outerNode.tooltip) {
    return outerNode.tooltip;
  }

  return false;
}

/**
 * Returns bullets associated to a highlighted date
 *
 * @param  {object} outerNode
 * @param  {object|Date} innerNode
 */
export function getHighlightBullets(outerNode, innerNode) {
  if (isObject(innerNode) && innerNode.bullets) {
    return innerNode.bullets;
  }

  if (outerNode.bullets) {
    return outerNode.bullets;
  }

  return [__EVENT_HIGHLIGHT_COLOR__];
}

/**
 * Returns normalized highlighted dates with proper props
 *
 * @param  {array} input
 * @returns {array}
 */
export function normalizeHighlights(input) {
  const highlighted = [];

  if (Array.isArray(input)) {
    // eslint-disable-next-line no-restricted-syntax
    for (const node of input) {
      if (node instanceof Date || typeof node === "string") {
        const saniDate = getSanitizedDate(node);
        highlighted.push({
          date: saniDate,
          localeStr: format(saniDate, __DEFAULT_DATE_FORMAT__),
          bullets: [__EVENT_HIGHLIGHT_COLOR__],
          tooltip: false
        });
      } else if (
        isObject(node) &&
        (node.date instanceof Date || typeof node.date === "string")
      ) {
        const saniDate = getSanitizedDate(node.date);
        highlighted.push({
          date: saniDate,
          localeStr: format(saniDate, __DEFAULT_DATE_FORMAT__),
          bullets: node.bullets || [__EVENT_HIGHLIGHT_COLOR__],
          tooltip: node.tooltip || false
        });
      } else if (isObject(node) && Array.isArray(node.dates)) {
        // eslint-disable-next-line no-restricted-syntax
        for (const innerNode of node.dates) {
          const d =
            isObject(innerNode) && innerNode.date ? innerNode.date : innerNode;
          const saniDate = getSanitizedDate(d);
          highlighted.push({
            date: saniDate,
            localeStr: format(saniDate, __DEFAULT_DATE_FORMAT__),
            tooltip: getHighlightTooltip(node, innerNode),
            bullets: getHighlightBullets(node, innerNode)
          });
        }
      }
    }
  }

  return uniqBy(highlighted, v => v.localeStr);
}
/**
 * Checks if year should be disabled
 *
 * @param  {Date|Number|String} minDate
 * @param  {Date|Number|String} maxDate
 * @param  {Number} year
 * @param  {Number} startYear
 * @param  {Number} endYear
 *
 * @returns {Boolean}
 */
export function isYearDisabled(minDate, maxDate, year, startYear, endYear) {
  if (!minDate) {
    minDate = new Date(startYear, 0, 1);
  }

  if (!maxDate) {
    maxDate = new Date(
      endYear,
      getDate(lastDayOfMonth(new Date(endYear, 11, 1)))
    );
  }

  const minDateYear = getYear(minDate);
  const maxDateYear = getYear(maxDate);

  return !isWithinInterval(new Date(year, 1, 1), {
    start: new Date(minDateYear, 1, 1),
    end: new Date(maxDateYear, 12, 31)
  });
}
/**
 * @param  {Date|Number|String} minDate
 * @param  {Date|Number|String} maxDate
 * @param  {Number} month
 * @param  {Number} year
 * @param  {Number} startYear
 * @param  {Number} endYear
 */
export function isMonthDisabled(
  minDate,
  maxDate,
  month,
  year,
  startYear,
  endYear
) {
  if (!minDate) {
    minDate = new Date(startYear, 0, 1);
  }

  if (!maxDate) {
    maxDate = new Date(
      endYear,
      11,
      getDate(lastDayOfMonth(new Date(endYear, 11, 1)))
    );
  }

  const lastDayOfThisMonth = getDate(lastDayOfMonth(new Date(year, month, 1)));
  for (let i = 1; i <= lastDayOfThisMonth; i++) {
    if (
      isWithinInterval(new Date(year, month, i), {
        start: minDate,
        end: maxDate
      })
    ) {
      return false;
    }
  }

  return true;
}
